import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLocation } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ExpiryWarning from '../components/ExpiryWarning';
import { getAvailableModels, formatPlanName } from '../utils/planAccess';

const DashboardPage = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [successMessage, setSuccessMessage] = useState('');
  const hasPaidPlan = Boolean(user?.subscription_plan);

  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      // Clear the message from location state
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const dismissMessage = () => {
    setSuccessMessage('');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navbar />
      
      <main className="flex-grow max-w-7xl mx-auto px-4 py-8">
        {/* Expiry Warning */}
        <ExpiryWarning user={user} />
        
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
            <button
              onClick={logout}
              className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg transition-colors"
            >
              Logout
            </button>
          </div>
          
          {/* Success Message */}
          {successMessage && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h2 className="text-xl font-semibold text-green-800 mb-2">
                    Payment Successful! 🎉
                  </h2>
                  <p className="text-green-700">
                    {successMessage}
                  </p>
                </div>
                <button
                  onClick={dismissMessage}
                  className="text-green-600 hover:text-green-800 ml-4"
                >
                  ✕
                </button>
              </div>
            </div>
          )}

          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <h2 className="text-xl font-semibold text-green-800 mb-2">
              Welcome back, {user?.first_name || user?.email}!
            </h2>
            <p className="text-green-700">
              You are successfully logged in to your TrendVector account.
            </p>
          </div>
          
          <div className={`grid grid-cols-1 ${hasPaidPlan ? 'md:grid-cols-4' : 'md:grid-cols-3'} gap-6`}>
            {hasPaidPlan && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-blue-800 mb-2">Investment Models</h3>
                <p className="text-blue-700 mb-4">
                  Your {formatPlanName(user?.subscription_plan)} gives you access to:
                </p>
                <div className="space-y-2">
                  {(() => {
                    const availableModels = getAvailableModels(user?.subscription_plan);
                    return Object.values(availableModels)
                      .filter(model => model.available)
                      .map(model => (
                        <a
                          key={model.path}
                          href={model.path}
                          className="block text-blue-600 hover:text-blue-800 font-medium"
                        >
                          {model.icon} {model.name} →
                        </a>
                      ));
                  })()}
                </div>
              </div>
            )}
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-green-800 mb-2">Your Subscription</h3>
              <p className="text-green-700 mb-4">
                {user?.subscription_plan ? 
                  `Current Plan: ${formatPlanName(user.subscription_plan)}` : 
                  'No active subscription'}
              </p>
              <a 
                href="/pricing" 
                className="inline-block bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Renew / Upgrade Plan →
              </a>
            </div>
            
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-purple-800 mb-2">Your Profile</h3>
              <p className="text-purple-700 mb-4">Manage your account settings and preferences.</p>
              <a href="/profile" className="text-purple-600 hover:text-purple-800 font-medium">
                View Profile →
              </a>
            </div>
            
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-orange-800 mb-2">Results History</h3>
              <p className="text-orange-700 mb-4">Track and analyze your historical scan results with performance tracking.</p>
              <div className="flex flex-col gap-2">
                <a href="/results-history" className="text-orange-600 hover:text-orange-800 font-medium">
                  View Results History →
                </a>
                <a href="/tools/nifty-signal-history" className="text-orange-600 hover:text-orange-800 font-medium">
                  View NIFTY Signal History →
                </a>
                <a href="/tools/nifty-signal-dashboard" className="text-orange-600 hover:text-orange-800 font-medium">
                  NIFTY Signal Analysis Dashboard →
                </a>
                <a href="/tools/reversal-candle-labeler" className="text-orange-600 hover:text-orange-800 font-medium">
                  Reversal Candle Labeler →
                </a>
                <a href="/tools/price-prediction-history" className="text-orange-600 hover:text-orange-800 font-medium">
                  View Price Prediction History →
                </a>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default DashboardPage;
